import React, { useState, useEffect } from "react";
import PageHeader from "../../components/PageHeader";
import ApiCall from "../../utils/apicall";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Container,
  Stack,
  Box,
  Grid,
  IconButton,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Paper,
  Table,
  MenuItem,
  TablePagination,
  TextField,
  Button,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Breadcrumbs,
  TableContainer,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import Iconify from "../../components/iconify";
import { BrandedDialogHeader, SectionHeader, FIELD_SX } from "../../components/WeldAssure/BrandedForm";
import { validateCompanyForm } from "../../utils/companyValidations";

const COMPANY_TYPES = ["CUSTOMER", "VENDOR", "SUBCONTRACTOR", "FABRICATOR", "INSPECTION_BODY", "OTHER"];
const COMPANY_TYPE_LABEL = { CUSTOMER: "Customer", VENDOR: "Vendor", SUBCONTRACTOR: "Subcontractor", FABRICATOR: "Fabricator", INSPECTION_BODY: "Inspection Body", OTHER: "Other" };
const COMPANY_REL_TYPES = ["Parent Company", "Subsidiary", "Partner", "Vendor", "Customer", "Subcontractor", "Affiliate", "Other"];
const EMPTY_COMPANY = {
  name: "", email: "", address: "", phone: "", fax: "", industry: "", website: "", tax_id: "",
  company_type: "", address_line2: "", city: "", state: "", postal_code: "", country: "", notes: "", relationships: [],
};

function CompanyRelationships({ value, onChange }) {
  const rels = Array.isArray(value) ? value : [];
  const set = (i, patch) => onChange(rels.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <Box>
      {rels.map((r, i) => (
        <Stack key={i} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }} alignItems="center">
          <TextField select size="small" label="Relationship" sx={{ ...FIELD_SX, minWidth: 180 }} value={r.type || ""} onChange={(e) => set(i, { type: e.target.value })}>
            <MenuItem value="">&mdash; Type &mdash;</MenuItem>
            {COMPANY_REL_TYPES.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
          </TextField>
          <TextField size="small" fullWidth label="Related company / person" sx={FIELD_SX} value={r.name || ""} onChange={(e) => set(i, { name: e.target.value })} />
          <IconButton size="small" color="error" onClick={() => onChange(rels.filter((_, idx) => idx !== i))}><Iconify icon="eva:close-fill" /></IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => onChange([...rels, { type: "", name: "" }])} sx={{ mt: 0.5 }}>Add relationship</Button>
    </Box>
  );
}

const TABLE_HEAD = [
  { id: "name", label: "Name", alignRight: false },
  { id: "email", label: "Email", alignRight: false },
  { id: "address", label: "Address", alignRight: false },
  { id: "phone", label: "Phone", alignRight: false },
  { id: "fax", label: "Fax", alignRight: false },
];

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [page, setPage] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
  const [openUpdateModel, setOpenUpdateModel] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: userPermissions } = loginUser.decodedToken;
  const canAddCompnay = hasPermission(userPermissions, PERMISSIONS.ADD_Company);
  const canEditCompany = hasPermission(
    userPermissions,
    PERMISSIONS.Edit_Company
  );
  const canDeleteCompany = hasPermission(
    userPermissions,
    PERMISSIONS.Delete_Company
  );

  const [open, setOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ ...EMPTY_COMPANY });

  const handleEdit = async (company) => {
    const response = await ApiCall.get(`/company/${company}`);
    const companyData = response.data.data.company;
    setOpenUpdateModel(true);
    setEditFormData({ ...EMPTY_COMPANY, ...companyData, relationships: companyData.relationships || [] });
  };

  const fetchCompanies = async () => {
    try {
      const response = await ApiCall.get("/company");
      setCompanies(response.data.data.companies);
      setSearchResults(response.data.data.companies);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  useEffect(() => {
    const fetchCompaniess = async () => {
      try {
        const response = await ApiCall.get("/company");
        setCompanies(response.data.data.companies);
        setSearchResults(response.data.data.companies);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompaniess();
  }, [setLoading]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await ApiCall.get("/company", {
          params: {
            page: page + 1,
          },
        });
        setCompanies(response.data.data.companies);
      } catch (error) {
        console.log("err", error);
      }
    };
    fetchPage();
  }, [page]);

  const handleEditCompanySubmit = async () => {
    const values = validateCompanyForm(editFormData);
    setFormErrors(values);
    if (Object.keys(values).length === 0) {
      try {
        const response = await ApiCall.put(
          `/company/${editFormData.id}`,
          editFormData
        );
        if (response.status === 200) {
          fetchCompanies();
          toast("Company Updated Successfully!");
          setEditFormData({ ...EMPTY_COMPANY });
          handleClose();
          setOpenUpdateModel(false);
        }
      } catch (error) {
        console.error("Error editing company:", error);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await ApiCall.delete(`/company/${id}`);
      if (response) {
        fetchCompanies();
        toast("Company Deleted Successfully!");
        setCompanies((company) => company.filter((order) => order.id !== id));
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const [formData, setFormData] = useState({ ...EMPTY_COMPANY });

  const CompanyForm = ({ data, set }) => (
    <Grid container spacing={1.5}>
      <Grid item xs={12}><SectionHeader>Company</SectionHeader></Grid>
      <Grid item xs={12} sm={6}><TextField label="Company Name" fullWidth size="small" sx={FIELD_SX} value={data.name || ""} onChange={(e) => set({ name: e.target.value })} error={formErrors.name !== undefined} helperText={formErrors.name} /></Grid>
      <Grid item xs={12} sm={6}><TextField select fullWidth size="small" sx={FIELD_SX} label="Company Type" value={data.company_type || ""} onChange={(e) => set({ company_type: e.target.value })}><MenuItem value="">&mdash; None &mdash;</MenuItem>{COMPANY_TYPES.map((t) => (<MenuItem key={t} value={t}>{COMPANY_TYPE_LABEL[t]}</MenuItem>))}</TextField></Grid>
      <Grid item xs={12} sm={6}><TextField label="Industry / Trade" fullWidth size="small" sx={FIELD_SX} value={data.industry || ""} onChange={(e) => set({ industry: e.target.value })} /></Grid>
      <Grid item xs={12} sm={6}><TextField label="Website" fullWidth size="small" sx={FIELD_SX} value={data.website || ""} onChange={(e) => set({ website: e.target.value })} /></Grid>

      <Grid item xs={12}><SectionHeader>Contact</SectionHeader></Grid>
      <Grid item xs={12} sm={4}><TextField label="Main Phone" fullWidth size="small" sx={FIELD_SX} value={data.phone || ""} onChange={(e) => set({ phone: e.target.value })} /></Grid>
      <Grid item xs={12} sm={4}><TextField label="Fax" fullWidth size="small" sx={FIELD_SX} value={data.fax || ""} onChange={(e) => set({ fax: e.target.value })} /></Grid>
      <Grid item xs={12} sm={4}><TextField label="Email" fullWidth size="small" sx={FIELD_SX} value={data.email || ""} onChange={(e) => set({ email: e.target.value })} error={formErrors.email !== undefined} helperText={formErrors.email} /></Grid>

      <Grid item xs={12}><SectionHeader>Address</SectionHeader></Grid>
      <Grid item xs={12} sm={8}><TextField label="Street" fullWidth size="small" sx={FIELD_SX} value={data.address || ""} onChange={(e) => set({ address: e.target.value })} /></Grid>
      <Grid item xs={12} sm={4}><TextField label="Suite / Unit" fullWidth size="small" sx={FIELD_SX} value={data.address_line2 || ""} onChange={(e) => set({ address_line2: e.target.value })} /></Grid>
      <Grid item xs={12} sm={5}><TextField label="City" fullWidth size="small" sx={FIELD_SX} value={data.city || ""} onChange={(e) => set({ city: e.target.value })} /></Grid>
      <Grid item xs={6} sm={3}><TextField label="State" fullWidth size="small" sx={FIELD_SX} value={data.state || ""} onChange={(e) => set({ state: e.target.value })} /></Grid>
      <Grid item xs={6} sm={4}><TextField label="ZIP / Postal" fullWidth size="small" sx={FIELD_SX} value={data.postal_code || ""} onChange={(e) => set({ postal_code: e.target.value })} /></Grid>
      <Grid item xs={12} sm={6}><TextField label="Country" fullWidth size="small" sx={FIELD_SX} value={data.country || ""} onChange={(e) => set({ country: e.target.value })} /></Grid>

      <Grid item xs={12}><SectionHeader>Tax &amp; Notes</SectionHeader></Grid>
      <Grid item xs={12} sm={6}><TextField label="Tax ID" fullWidth size="small" sx={FIELD_SX} value={data.tax_id || ""} onChange={(e) => set({ tax_id: e.target.value })} /></Grid>
      <Grid item xs={12}><TextField label="Notes" fullWidth multiline rows={2} size="small" sx={FIELD_SX} value={data.notes || ""} onChange={(e) => set({ notes: e.target.value })} /></Grid>

      <Grid item xs={12}><SectionHeader>Relationships</SectionHeader></Grid>
      <Grid item xs={12}><CompanyRelationships value={data.relationships} onChange={(rels) => set({ relationships: rels })} /></Grid>
    </Grid>
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleOpenModel = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setFormErrors("");
    setFormData({ ...EMPTY_COMPANY });
    setOpenUpdateModel(false);
    setOpen(false);
  };

  const handleAddCompanySubmit = async () => {
    const values = validateCompanyForm(formData);
    setFormErrors(values);
    if (Object.keys(values).length === 0) {
      try {
        const response = await ApiCall.post("/company", formData);
        if (response) {
          fetchCompanies();
          toast("Company Added Successfully!");
          setFormData({ ...EMPTY_COMPANY });
          setFormErrors("");
          handleClose();
        }
      } catch (error) {
        console.error("Error adding company:", error);
      }
    }
  };
  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = async () => {
    try {
      const response = await ApiCall.get("/company", {
        params: {
          name: filterName,
        },
      });
      setSearchResults(response.data.data.companies);
    } catch (error) {}
  };
  useEffect(() => {
    const handleSearchs = async () => {
      try {
        const response = await ApiCall.get("/company", {
          params: {
            name: filterName,
          },
        });
        setSearchResults(response.data.data.companies);
      } catch (error) {}
    };
    handleSearchs();
  }, [filterName]);

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - searchResults.length) : 0;
  const isNotFound = !searchResults.length && !!filterName;

  // Utility function to format phone number as (123) 456-7890
  function formatPhoneNumber(phoneNumberString) {
    var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      var intlCode = "+1 ";
      return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
    }
    return null;
  }
  function formatfaxNumber(faxNumberString) {
    var cleaned = ("" + faxNumberString).replace(/\D/g, "");
    var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return ["+1", "-", match[2], "-", match[3], "-", match[4]].join("");
    }
    return null;
  }

  return (
    <div>
      <Helmet>
        <title>Companies | SFM</title>
      </Helmet>
      <div>
        <Container>
          <PageHeader
            breadcrumbs={[{ label: "Companies" }]}
            title="Companies"
            action={canAddCompnay && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleOpenModel}
              >
                Add New Company
              </Button>
            )}
            guide={{
              why: "Keep one record per company you do business with — so jobs, contacts, and POs all point to the same place.",
              who: "Whoever manages customer and supplier records — sales, purchasing, and admin.",
              what: "Your directory of customers, vendors, subcontractors, fabricators, and inspection bodies.",
              how: "Add a company with its address, contacts, tax details, and relationships.",
              when: "When you take on a new customer, vendor, or subcontractor.",
              standard: "ISO 9001:2015 §8.4",
            }}
          />
          <TableContainer component={Paper}>
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TextField
                label="Search companies..."
                variant="outlined"
                margin="normal"
                value={filterName}
                onChange={handleFilterByName}
                onKeyDown={handleEnterKeyPress}
              />
              <TablePagination
                rowsPerPageOptions={[10, 25]}
                component="div"
                count={companies.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
            <Table>
              <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
                <BrandedDialogHeader title="Add New Company" subtitle="Companies · SFM" onClose={handleClose} />
                <DialogContent sx={{ pt: 2.5 }}>
                  <CompanyForm data={formData} set={(patch) => setFormData({ ...formData, ...patch })} />
                </DialogContent>
                <DialogActions sx={{ bgcolor: "#F7F9FC", borderTop: "1px solid #E8ECF0", px: 3, py: 1.75 }}>
                  <Button onClick={handleClose} sx={{ color: "text.secondary" }}>Cancel</Button>
                  <Button variant="contained" onClick={handleAddCompanySubmit}>Create</Button>
                </DialogActions>
              </Dialog>

              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(searchResults) &&
                  searchResults
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order) => (
                      <TableRow key={order.id}>
                        {TABLE_HEAD.map((column) => (
                          <TableCell key={column.id}>
                            {/* Check if column is phone or fax, if yes, format the value */}
                            {column.id === "phone"
                              ? formatPhoneNumber(order[column.id])
                              : column.id === "fax"
                              ? formatfaxNumber(order[column.id])
                              : order[column.id]}
                          </TableCell>
                        ))}

                        <TableCell align="right" style={{ display: "flex" }}>
                          {canEditCompany && (
                            <MenuItem
                              variant="contained"
                              onClick={() => handleEdit(order.id)}
                            >
                              <Iconify icon={"eva:edit-fill"} />
                            </MenuItem>
                          )}
                          {openUpdateModel && (
                            <Dialog
                              open={openUpdateModel}
                              onClose={handleClose}
                              maxWidth="sm"
                              fullWidth
                              PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
                            >
                              <BrandedDialogHeader title="Edit Company" subtitle="Companies · SFM" onClose={handleClose} />
                              <DialogContent sx={{ pt: 2.5 }}>
                                <CompanyForm data={editFormData} set={(patch) => setEditFormData({ ...editFormData, ...patch })} />
                              </DialogContent>
                              <DialogActions sx={{ bgcolor: "#F7F9FC", borderTop: "1px solid #E8ECF0", px: 3, py: 1.75 }}>
                                <Button onClick={handleClose} sx={{ color: "text.secondary" }}>Cancel</Button>
                                <Button variant="contained" onClick={handleEditCompanySubmit}>Update</Button>
                              </DialogActions>
                            </Dialog>
                          )}
                          {canDeleteCompany && (
                            <MenuItem
                              sx={{ color: "error.main" }}
                              onClick={() => setSelectedItem(order)}
                            >
                              <Iconify icon={"eva:trash-2-outline"} />
                            </MenuItem>
                          )}
                        </TableCell>
                        <Dialog
                          open={selectedItem !== null}
                          onClose={() => setSelectedItem(null)}
                        >
                          <DialogTitle>Delete Company</DialogTitle>
                          <DialogContent>
                            <Typography>
                              Are you sure you want to delete company {""}
                              {selectedItem?.company_name}?
                            </Typography>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={() => setSelectedItem(null)}>
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                handleDelete(selectedItem.id);
                                setSelectedItem(null);
                              }}
                              sx={{ color: "error.main" }}
                            >
                              Delete
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </TableRow>
                    ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={4} />
                  </TableRow>
                )}
              </TableBody>
              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <Paper
                        sx={{
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="h6" paragraph>
                          Not found
                        </Typography>

                        <Typography variant="body2">
                          No results found for &nbsp;
                          <strong>&quot;{filterName}&quot;</strong>.
                          <br /> Try checking for typos or using complete words.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Container>
      </div>
    </div>
  );
};

export default CompanyList;
